"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";

// Actions jouées par le terminal, synchronisées avec l'apparition des blocs
// dans la preview (le bloc "phase N" apparaît quand le terminal atteint la phase N).
const ACTIONS = [
    { phase: 2, file: "components/Hero.jsx", labelKey: "ClaudeCodeLive_action_hero" },
    {
        phase: 3,
        file: "components/Pricing.jsx",
        labelKey: "ClaudeCodeLive_action_pricing",
    },
];

const PRICING = [
    { name: "Starter", price: "9" },
    { name: "Pro", price: "29", featured: true },
    { name: "Business", price: "79" },
];

export default function ClaudeCodeLiveBuild() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-120px" });

    const prompt = t("ClaudeCodeLive_prompt");

    const [typed, setTyped] = useState("");
    const [phase, setPhase] = useState(0); // 0 typing · 1 thinking · 2 hero · 3 pricing · 4 form · 5 done

    // runId force le rejeu de la séquence (boucle "vivante").
    const [runId, setRunId] = useState(0);

    useEffect(() => {
        if (reduce) {
            setTyped(prompt);
            setPhase(5);
            return;
        }
        if (!inView) return;

        const timers = [];
        setTyped("");
        setPhase(0);

        let i = 0;
        const type = () => {
            i += 1;
            setTyped(prompt.slice(0, i));
            if (i < prompt.length) {
                timers.push(setTimeout(type, 34));
                return;
            }
            timers.push(setTimeout(() => setPhase(1), 500));
            timers.push(setTimeout(() => setPhase(2), 1300));
            timers.push(setTimeout(() => setPhase(3), 2150));
            timers.push(setTimeout(() => setPhase(4), 3050));
            timers.push(setTimeout(() => setRunId((r) => r + 1), 6200));
        };
        timers.push(setTimeout(type, 700));

        return () => timers.forEach(clearTimeout);
    }, [inView, runId, reduce, prompt]);

    const reveal = (custom = {}) =>
        reduce
            ? { initial: false }
            : {
                  initial: { opacity: 0, y: 10, ...custom },
                  animate: { opacity: 1, y: 0, scale: 1, x: 0 },
                  transition: { duration: 0.45, ease: "easeOut" },
              };

    return (
        <section id="claude-code-live" className="pt-16" ref={ref}>
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("ClaudeCodeLive_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("ClaudeCodeLive_intro")}
            </p>

            {/* === DÉMO VIVANTE === */}
            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 text-[11px] font-mono text-gray-400">
                        claude-code
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("ClaudeCodeLive_badge_live")}
                        </span>
                    </span>
                </div>

                <div className="relative grid lg:grid-cols-2">
                    {/* TERMINAL */}
                    <div className="p-5 sm:p-7 font-mono text-[12.5px] sm:text-[13px] leading-relaxed lg:border-r border-white/10 min-h-[340px]">
                        {/* Commande + prompt tapé */}
                        <div className="text-gray-300">
                            <span className="text-emerald-400">➜</span>{" "}
                            <span className="text-sky-400">~/mon-saas</span>{" "}
                            <span className="text-gray-500">claude</span>
                        </div>
                        <div className="mt-2 text-gray-100 break-words">
                            <span className="text-orange-400">{">"}</span> {typed}
                            {phase === 0 && (
                                <motion.span
                                    aria-hidden
                                    className="inline-block w-2 h-[15px] bg-orange-400 ml-0.5 align-middle"
                                    animate={{ opacity: [1, 1, 0, 0] }}
                                    transition={{
                                        repeat: Infinity,
                                        duration: 1,
                                        times: [0, 0.5, 0.5, 1],
                                    }}
                                />
                            )}
                        </div>

                        {/* Lecture + génération */}
                        {phase >= 1 && (
                            <motion.div {...reveal()} className="mt-4 text-gray-400">
                                <span className="text-orange-400">●</span>{" "}
                                {t("ClaudeCodeLive_read")}
                            </motion.div>
                        )}

                        {/* Actions / fichiers créés */}
                        <div className="mt-1.5 flex flex-col gap-1.5">
                            {ACTIONS.map((a) => {
                                if (phase < a.phase) return null;
                                const done = phase > a.phase;
                                return (
                                    <motion.div
                                        key={a.file}
                                        {...reveal()}
                                        className="flex items-center gap-2"
                                    >
                                        {done ? (
                                            <span className="text-emerald-400">
                                                ✓
                                            </span>
                                        ) : (
                                            <span className="inline-block w-3 h-3 rounded-full border-2 border-orange-400/30 border-t-orange-400 animate-spin" />
                                        )}
                                        <span className="text-gray-300">
                                            {t(a.labelKey)}
                                        </span>
                                        <span className="text-gray-500">
                                            {a.file}
                                        </span>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Spinner "génération" tant que ce n'est pas fini */}
                        {phase >= 1 && phase < 4 && (
                            <div className="mt-3 flex items-center gap-2 text-orange-300">
                                <span className="inline-block w-3 h-3 rounded-full border-2 border-orange-400/30 border-t-orange-400 animate-spin" />
                                <span>{t("ClaudeCodeLive_thinking")}</span>
                            </div>
                        )}

                        {/* Terminé */}
                        {phase >= 4 && (
                            <motion.div
                                {...reveal()}
                                className="mt-3 flex items-center gap-2 text-emerald-300"
                            >
                                <span>✓</span>
                                <span className="font-semibold">
                                    {t("ClaudeCodeLive_done")}
                                </span>
                            </motion.div>
                        )}
                    </div>

                    {/* PREVIEW NAVIGATEUR */}
                    <div className="p-5 sm:p-7">
                        <div className="rounded-2xl bg-white shadow-2xl overflow-hidden ring-1 ring-black/5 h-full flex flex-col">
                            {/* Barre d'adresse */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200 shrink-0">
                                <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                                <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                                <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                                <div className="ml-2 flex-1 flex items-center gap-1.5 bg-white rounded-md px-2.5 py-1 ring-1 ring-gray-200">
                                    <span className="text-[9px] text-gray-400">
                                        🔒
                                    </span>
                                    <span className="text-[10px] text-gray-500 font-mono">
                                        localhost:3000
                                    </span>
                                </div>
                            </div>

                            {/* Contenu de l'app — hauteur fixe : le navigateur ne change jamais de taille */}
                            <div className="relative p-4 sm:p-5 h-[300px] overflow-hidden">
                                {/* Skeleton tant que rien n'est généré */}
                                {phase < 2 && (
                                    <div className="animate-pulse space-y-3 pt-2">
                                        <div className="h-3 w-1/3 bg-gray-200 rounded" />
                                        <div className="h-8 w-3/4 bg-gray-200 rounded" />
                                        <div className="h-3 w-1/2 bg-gray-200 rounded" />
                                        <div className="h-9 w-28 bg-gray-200 rounded-lg mt-2" />
                                    </div>
                                )}

                                {/* Nav + Hero */}
                                {phase >= 2 && (
                                    <motion.div {...reveal({ y: 14 })}>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-1.5">
                                                <span className="w-4 h-4 rounded-md bg-gradient-to-br from-orange-400 to-amber-500" />
                                                <span className="text-[11px] font-bold text-gray-800">
                                                    {t(
                                                        "ClaudeCodeLive_preview_brand",
                                                    )}
                                                </span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="w-8 h-1.5 bg-gray-200 rounded-full" />
                                                <span className="w-8 h-1.5 bg-gray-200 rounded-full" />
                                                <span className="w-8 h-1.5 bg-gray-200 rounded-full" />
                                            </div>
                                        </div>
                                        <div className="text-center py-2">
                                            <h3 className="text-base sm:text-lg font-extrabold text-gray-900 leading-tight">
                                                {t(
                                                    "ClaudeCodeLive_preview_hero_title",
                                                )}
                                            </h3>
                                            <p className="text-[11px] text-gray-500 mt-1 mb-3">
                                                {t(
                                                    "ClaudeCodeLive_preview_hero_sub",
                                                )}
                                            </p>
                                            <span className="inline-block text-[11px] font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-1.5 rounded-lg shadow-sm">
                                                {t(
                                                    "ClaudeCodeLive_preview_cta",
                                                )}
                                            </span>
                                        </div>
                                    </motion.div>
                                )}

                                {/* Pricing */}
                                {phase >= 3 && (
                                    <motion.div
                                        {...reveal({ y: 14 })}
                                        className="grid grid-cols-3 gap-2 mt-4"
                                    >
                                        {PRICING.map((p) => (
                                            <div
                                                key={p.name}
                                                className={`rounded-lg p-2.5 text-center ring-1 ${
                                                    p.featured
                                                        ? "ring-orange-300 bg-orange-50"
                                                        : "ring-gray-200 bg-white"
                                                }`}
                                            >
                                                <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-500">
                                                    {p.name}
                                                </div>
                                                <div className="text-sm font-extrabold text-gray-900 mt-0.5">
                                                    {p.price}€
                                                    <span className="text-[8px] font-medium text-gray-400">
                                                        {t(
                                                            "ClaudeCodeLive_preview_price_month",
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </motion.div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("ClaudeCodeLive_caption")}
            </p>
        </section>
    );
}
